import React, {useEffect, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';

const SECTIONS = [
  {
    id: 'setup',
    title: '開始設定',
    emoji: '🎮',
    items: [
      ['選擇比賽模式', '選擇單打或雙打並輸入隊伍及球員名稱。'],
      ['選擇分數和賽制', '21 分三局兩勝可用於標準模式；11、15 和以 30 分為目標的選項屬自訂或練習模式。'],
      ['開始比賽', '確認設定後按「開始比賽」進入計分畫面。']
    ]
  },
  {
    id: 'scoring',
    title: '比賽計分',
    emoji: '🏆',
    items: [
      ['點擊比分區', '點擊左側或右側的大比分區，為對應隊伍增加一分。'],
      ['查看發球提示', '系統按發球方分數的奇偶提示左、右發球區。'],
      ['局末分及賽末分', '再得一分可能結束該局或整場比賽時，畫面會顯示提示。']
    ]
  },
  {
    id: 'sides',
    title: '換邊規則',
    emoji: '↔️',
    items: [
      ['局間換邊', '每局結束後按「下一局」，系統會交換畫面兩方的場區。'],
      ['下一局發球', '上一局勝方會在下一局先發球，並從右發球區開始。'],
      ['決勝局中途換邊', '標準 21 分模式在任一方先到 11 分時換邊；自訂模式使用指定分數一半向上取整的產品規則。'],
      ['手動換邊', '如畫面位置需要修正，可使用工具列的手動換邊功能。']
    ]
  },
  {
    id: 'tools',
    title: '其他工具',
    emoji: '⚙️',
    items: [
      ['撤銷及恢復', '可撤銷或恢復最近的計分、換邊或換局操作。'],
      ['計時及記錄', '工具列提供計時器、完整得分記錄及統計。'],
      ['本機恢復', '未完成比賽會保存在目前瀏覽器，最多保留 12 小時。']
    ]
  }
];

const AppGuideModal = ({open, onClose}) => {
  const [active, setActive] = useState('setup');
  const section = SECTIONS.find(item => item.id === active);

  useEffect(() => {
    if (!open) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
          <motion.button
            type="button"
            aria-label="關閉使用教學"
            className="absolute inset-0 bg-slate-950/90 backdrop-blur-md"
            onClick={onClose}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
          />
          <motion.article
            role="dialog"
            aria-modal="true"
            aria-labelledby="app-guide-title"
            className="relative z-10 flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#0a1220] shadow-2xl"
            initial={{opacity: 0, scale: 0.95, y: 20}}
            animate={{opacity: 1, scale: 1, y: 0}}
            exit={{opacity: 0, scale: 0.95, y: 20}}
          >
            <header className="flex items-center justify-between border-b border-white/10 px-6 py-5">
              <div>
                <h2 id="app-guide-title" className="text-xl font-black text-white">
                  如何使用羽球計分板？
                </h2>
                <p className="mt-1 text-xs text-white/50">
                  功能說明以目前程式行為為準
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="關閉使用教學"
                className="rounded-xl bg-white/5 p-2.5 text-xl text-white/60 hover:bg-white/10 hover:text-white"
              >
                ×
              </button>
            </header>

            <nav className="flex shrink-0 gap-2 overflow-x-auto border-b border-white/5 px-4 py-3">
              {SECTIONS.map(item => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => setActive(item.id)}
                  className={`shrink-0 rounded-xl border px-4 py-2.5 text-sm font-black ${
                    active === item.id
                      ? 'border-emerald-500/40 bg-emerald-500/25 text-emerald-300'
                      : 'border-white/10 bg-white/[0.04] text-white/50'
                  }`}
                >
                  {item.emoji} {item.title}
                </button>
              ))}
            </nav>

            <section className="flex-1 space-y-3 overflow-y-auto px-6 py-5">
              <h3 className="text-lg font-black text-white">
                {section.emoji} {section.title}
              </h3>
              {section.items.map(([title, description]) => (
                <div
                  key={title}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-4"
                >
                  <h4 className="text-sm font-black text-white">{title}</h4>
                  <p className="mt-2 text-sm leading-6 text-white/65">
                    {description}
                  </p>
                </div>
              ))}
            </section>
          </motion.article>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AppGuideModal;